import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import { getPost } from "../lib/api";
import { blogPostingSchema, breadcrumbSchema } from "../lib/seo";

export default function BlogPost({ brand }) {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        getPost(slug)
            .then((d) => !cancelled && setPost(d))
            .catch(() => !cancelled && setError("not found"));
        return () => {
            cancelled = true;
        };
    }, [slug]);
    const isStale = post && post.slug !== slug;

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    Post not found.
                </h1>
                <Link to="/blog" className="text-zen-olive underline">
                    Back to journal
                </Link>
            </div>
        );
    }
    if (!post || isStale)
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                Loading…
            </div>
        );

    const canonical = `https://weeddispensarynearme.com/blog/${post.slug}`;

    return (
        <>
            <SEOHead
                title={post.title}
                description={post.excerpt}
                canonical={canonical}
                image={post.image}
                type="article"
            />
            <SchemaJsonLd
                schema={[
                    blogPostingSchema(post, brand),
                    breadcrumbSchema([
                        { name: "Home", url: "https://weeddispensarynearme.com/" },
                        {
                            name: "Journal",
                            url: "https://weeddispensarynearme.com/blog",
                        },
                        { name: post.title, url: canonical },
                    ]),
                ]}
            />

            <article>
                <section
                    className="relative overflow-hidden bg-zen-elevated"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(40,48,31,0.4), rgba(40,48,31,0.95)), url('${post.image}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                >
                    <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-24 pb-20 sm:pt-32 sm:pb-32">
                        <Link
                            to="/blog"
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zen-cream-muted hover:text-zen-cream mb-6"
                        >
                            <ArrowLeft size={12} /> Back to journal
                        </Link>
                        <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive-light mb-6">
                            {new Date(post.date).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                            })}{" "}
                            · {post.read_time}
                        </p>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.02] tracking-tight max-w-3xl">
                            {post.title}
                        </h1>
                    </div>
                </section>

                <section className="bg-zen-bg max-w-3xl mx-auto px-6 sm:px-10 py-14">
                    <div className="space-y-6 text-lg text-zen-ink leading-relaxed">
                        {post.body.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                    <div className="mt-16 border-t border-zen-ink/10 pt-8 flex flex-wrap gap-2">
                        {post.tags.map((t) => (
                            <span
                                key={t}
                                className="px-3 py-1 border border-zen-ink/20 text-[10px] uppercase tracking-[0.25em] text-zen-ink-soft"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </section>
            </article>
        </>
    );
}
